// PARAQTA — Парақтау Феед v2 (Threads pattern, Stage 6.4).
//
// loadFeedV2() — fetch /api/feed/v2, render posts list, init banner,
// setup infinite scroll. 4 post type: review, quote, book-finished,
// review+spoiler.
//
// Pattern reuse:
//   - Onboarding banner: бар #feedOnboarding + paraqta_feed_onboarded
//   - Modal: .modal-ov / .open class
//
// loadFeed() — legacy entry, loadFeedV2-ге delegate.

import { api } from '../../core/api.js';
import { show } from '../../core/router.js';
import { openBookPage } from '../book-page/index.js';
import { toast } from '../../utils/toast.js';
import { esc } from '../../utils/format.js';

const $ = (sel, r = document) => r.querySelector(sel);
const root = () => document.getElementById('pg-feed');

const state = {
  tab: 'all',
  posts: [],
  offset: 0,
  hasMore: true,
  loading: false,
  reqId: 0,
  observer: null,
};

function setF(name, value) {
  const el = $(`[data-field="${name}"]`, root());
  if (el) el.textContent = value;
}

function fmtCount(n) {
  return (Number(n) || 0).toString();
}

// ── Onboarding banner — бар pattern reuse ──────────────────
function initOnboardingBanner() {
  const banner = document.getElementById('feedOnboarding');
  if (!banner) return;
  if (localStorage.getItem('paraqta_feed_onboarded') === 'true') {
    banner.style.display = 'none';
  }
  // Dismiss handler already in events.js — байланыс орнатылған
}

// ── Tab UI ───────────────────────────────────────────────────
function renderTabs(active) {
  root().querySelectorAll('[data-action="feed-tab"]').forEach(b => {
    const isActive = b.dataset.feedTab === active;
    if (isActive) {
      b.dataset.active = 'true';
      b.style.background = '#1C1A16';
      b.style.color = '#F7F2EA';
      b.style.border = 'none';
      b.style.fontWeight = '500';
    } else {
      delete b.dataset.active;
      b.style.background = 'transparent';
      b.style.color = 'color-mix(in srgb, var(--ink) 65%, transparent)';
      b.style.border = '0.5px solid color-mix(in srgb, var(--ink) 12%, transparent)';
      b.style.fontWeight = '';
    }
  });
}

// ── Post renderers ──────────────────────────────────────────
function renderReactionsRow(post) {
  const s = post.stats || {};
  const isReview = post.post_type === 'review';
  const isQuote = post.post_type === 'quote';
  const isFinished = post.post_type === 'book-finished';
  const likeEnabled = isReview || isQuote;

  const likeIcon = s.is_liked_by_me ? 'ti-heart-filled' : 'ti-heart';
  const likeStyle = s.is_liked_by_me
    ? 'font-size: 17px; color: #f43f5e;'
    : 'font-size: 17px; opacity: 0.7;';
  const likeCountStyle = s.is_liked_by_me
    ? 'font-size: 12px; color: #f43f5e; font-weight: 500;'
    : 'font-size: 12px; opacity: 0.7;';

  const saveIcon = s.is_saved_by_me ? 'ti-bookmark-filled' : 'ti-bookmark';
  const saveStyle = s.is_saved_by_me
    ? 'font-size: 17px; color: var(--ac);'
    : 'font-size: 17px; opacity: 0.7;';

  let html = `<div style="display: flex; align-items: center; gap: 18px; padding-top: 10px; border-top: 0.5px solid color-mix(in srgb, var(--ink) 6%, transparent);">`;

  if (likeEnabled) {
    html += `
      <button data-action="post-like" data-post-type="${post.post_type}" data-post-id="${post.post_id}" style="display: flex; align-items: center; gap: 5px; background: none; border: none; cursor: pointer; padding: 0;">
        <i class="ti ${likeIcon}" style="${likeStyle}"></i>
        <span data-field="post-likes-count" style="${likeCountStyle}">${fmtCount(s.likes || 0)}</span>
      </button>`;
  } else {
    // Book-finished — like жоқ; share/heart жоқ ynstead
    html += `
      <button data-action="post-like" data-post-type="${post.post_type}" data-post-id="${post.post_id}" disabled style="display: flex; align-items: center; gap: 5px; background: none; border: none; cursor: not-allowed; padding: 0; opacity: 0.4;">
        <i class="ti ti-heart" style="font-size: 17px;"></i>
        <span style="font-size: 12px;">${fmtCount(s.likes || 0)}</span>
      </button>`;
  }

  html += `
    <button data-action="post-comments" style="display: flex; align-items: center; gap: 5px; background: none; border: none; cursor: pointer; padding: 0;">
      <i class="ti ti-message-circle" style="font-size: 17px; opacity: 0.7;"></i>
      <span style="font-size: 12px; opacity: 0.7;">${fmtCount(s.comments || 0)}</span>
    </button>
    <button data-action="post-save" data-post-type="${post.post_type}" data-post-id="${post.post_id}" style="display: flex; align-items: center; gap: 5px; background: none; border: none; cursor: pointer; padding: 0;">
      <i class="ti ${saveIcon}" style="${saveStyle}"></i>
    </button>`;

  if (isFinished) {
    html += `
      <button data-action="open-book" ${post.book.slug ? `data-slug="${esc(post.book.slug)}"` : ''} style="margin-left: auto; padding: 6px 12px; background: color-mix(in srgb, var(--gn) 8%, transparent); color: var(--gn); border: 0.5px solid color-mix(in srgb, var(--gn) 20%, transparent); border-radius: 999px; font-size: 11px; cursor: pointer; font-weight: 500;">
        Мен де оқимын
      </button>`;
  } else {
    html += `
      <button data-action="post-share" style="display: flex; align-items: center; gap: 5px; background: none; border: none; cursor: pointer; padding: 0; margin-left: auto;">
        <i class="ti ti-share-2" style="font-size: 17px; opacity: 0.5;"></i>
      </button>`;
  }

  html += `</div>`;
  return html;
}

function renderUserHeader(post) {
  const u = post.user || {};
  const jandos = u.is_jandos
    ? '<i class="ti ti-feather" style="font-size: 11px; color: var(--gold);" title="Жан дос"></i>'
    : '';
  return `
    <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 12px;">
      <button data-action="open-user" data-user-id="${u.telegram_id || ''}" style="display: flex; align-items: center; gap: 10px; background: none; border: none; cursor: pointer; padding: 0; flex: 1; min-width: 0;">
        <div style="width: 36px; height: 36px; border-radius: 50%; background: ${esc(u.color_gradient || 'linear-gradient(135deg, #C9A96E, #8B5E34)')}; color: var(--bg); display: flex; align-items: center; justify-content: center; font-family: Georgia, serif; font-size: 13px; font-weight: 500; flex-shrink: 0;">${esc(u.initials || '?')}</div>
        <div style="flex: 1; min-width: 0; text-align: left;">
          <div style="display: flex; align-items: center; gap: 5px;">
            <span style="font-size: 13px; font-weight: 500;">${esc(u.name || 'Оқырман')}</span>
            ${jandos}
          </div>
          <div style="font-size: 10.5px; opacity: 0.55;">${u.username ? '@' + esc(u.username) + ' · ' : ''}${esc(post.time_ago || '')}</div>
        </div>
      </button>
      <button data-action="post-menu" style="width: 28px; height: 28px; border-radius: 50%; background: none; border: none; cursor: pointer; opacity: 0.5;">
        <i class="ti ti-dots" style="font-size: 14px;"></i>
      </button>
    </div>`;
}

function renderBookCardCompact(post) {
  const b = post.book || {};
  const yearStr = b.year ? ` · ${b.year}` : '';
  return `
    <button data-action="open-book" ${b.slug ? `data-slug="${esc(b.slug)}"` : ''} style="display: flex; align-items: center; gap: 10px; padding: 10px; background: color-mix(in srgb, var(--bg) 60%, transparent); border: 0.5px solid color-mix(in srgb, var(--ink) 6%, transparent); border-radius: 10px; margin-bottom: 12px; cursor: pointer; width: 100%; text-align: left;">
      <div style="width: 40px; height: 58px; background: ${esc(b.cover_gradient || 'linear-gradient(135deg, #8B5E34 0%, #6B4423 100%)')}; border-radius: 3px; flex-shrink: 0; position: relative; overflow: hidden;">
        <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-family: Georgia, serif; font-size: 7px; font-style: italic; color: var(--bg); padding: 0 3px; text-align: center;">${esc(b.cover_text || b.title || '')}</div>
      </div>
      <div style="flex: 1; min-width: 0;">
        <div style="font-family: Georgia, serif; font-size: 13px; font-weight: 500; line-height: 1.2; margin-bottom: 1px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${esc(b.title || '')}</div>
        <div style="font-size: 10.5px; opacity: 0.55; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${esc(b.author || '')}${yearStr}</div>
      </div>
      <i class="ti ti-chevron-right" style="font-size: 13px; opacity: 0.3;"></i>
    </button>`;
}

function renderReviewPost(post) {
  const c = post.content || {};
  const ratingHtml = c.rating
    ? `<div style="display: flex; align-items: center; gap: 6px; margin-bottom: 10px;">
        <div style="display: flex; gap: 1px; color: var(--gold); font-size: 13px;">
          ${'<i class="ti ti-star-filled"></i>'.repeat(Math.max(0, Math.min(5, c.rating)))}${'<i class="ti ti-star" style="opacity:0.25"></i>'.repeat(Math.max(0, 5 - c.rating))}
        </div>
        <span style="font-size: 11px; opacity: 0.6;">${c.rating}/5</span>
      </div>`
    : '';

  let textHtml;
  if (c.auto_hide_spoiler) {
    textHtml = renderSpoilerBox();
  } else if (c.is_truncated) {
    textHtml = `
      <div data-field="post-text" data-is-truncated="true" style="font-size: 14px; line-height: 1.6; margin-bottom: 8px; color: var(--ink); white-space: pre-wrap;">${esc(c.text_preview || '')}</div>
      <button data-action="expand-post" data-post-type="review" data-post-id="${post.post_id}" style="background: none; border: none; cursor: pointer; padding: 0 0 12px; color: var(--ac); font-size: 12.5px; font-weight: 500; font-family: inherit; display: block;">Толық оқу →</button>`;
  } else {
    textHtml = `<div data-field="post-text" style="font-size: 14px; line-height: 1.6; margin-bottom: 12px; color: var(--ink); white-space: pre-wrap;">${esc(c.text || '')}</div>`;
  }

  return `
    <div data-post-type="review" data-post-id="${post.post_id}" ${c.has_spoiler ? 'data-has-spoiler="true"' : ''} style="background: var(--card); padding: 16px; border-bottom: 0.5px solid color-mix(in srgb, var(--ink) 6%, transparent);">
      ${renderUserHeader(post)}
      ${renderBookCardCompact(post)}
      ${ratingHtml}
      ${textHtml}
      ${renderReactionsRow(post)}
    </div>`;
}

function renderSpoilerBox() {
  return `
    <div data-section="spoiler-content" data-spoiler-hidden="true" data-action="reveal-spoiler" style="background: color-mix(in srgb, var(--ink) 4%, transparent); border: 0.5px dashed color-mix(in srgb, var(--ink) 20%, transparent); border-radius: 10px; padding: 14px; text-align: center; margin-bottom: 14px; cursor: pointer;">
      <i class="ti ti-eye-off" style="font-size: 20px; opacity: 0.5; display: block; margin-bottom: 6px;"></i>
      <div style="font-size: 12px; font-weight: 500; margin-bottom: 4px;">Мазмұн жасырылған</div>
      <div style="font-size: 10.5px; opacity: 0.55;">Спойлер бар — ашу үшін басыңыз</div>
    </div>`;
}

function renderQuotePost(post) {
  const c = post.content || {};
  const b = post.book || {};
  const pageStr = c.page_num ? ` · ${c.page_num}-бет` : '';
  return `
    <div data-post-type="quote" data-post-id="${post.post_id}" style="background: var(--card); padding: 16px; border-bottom: 0.5px solid color-mix(in srgb, var(--ink) 6%, transparent);">
      ${renderUserHeader(post)}
      <div style="background: linear-gradient(180deg, color-mix(in srgb, var(--gold) 10%, transparent) 0%, transparent 100%); border-left: 2px solid #C9A96E; border-radius: 0 12px 12px 0; padding: 18px 16px; margin-bottom: 14px;">
        <i class="ti ti-quote" style="font-size: 16px; color: var(--gold); display: block; margin-bottom: 6px;"></i>
        <p style="font-family: Georgia, serif; font-size: 16px; font-style: italic; line-height: 1.55; margin: 0 0 12px; color: var(--ink);">«${esc(c.quote_text || c.text || '')}»</p>
        <button data-action="open-book" ${b.slug ? `data-slug="${esc(b.slug)}"` : ''} style="display: flex; align-items: center; gap: 5px; background: none; border: none; cursor: pointer; padding: 0;">
          <span style="font-size: 10.5px; opacity: 0.6; font-style: italic;">${esc(b.author || '')} · «${esc(b.title || '')}»${pageStr}</span>
          <i class="ti ti-arrow-right" style="font-size: 11px; opacity: 0.6;"></i>
        </button>
      </div>
      ${renderReactionsRow(post)}
    </div>`;
}

function renderBookFinishedPost(post) {
  const u = post.user || {};
  const b = post.book || {};
  const pagesStr = b.pages ? `${b.pages} бет` : '';
  return `
    <div data-post-type="book-finished" data-post-id="${post.post_id}" style="background: linear-gradient(180deg, color-mix(in srgb, var(--gn) 4%, transparent) 0%, color-mix(in srgb, var(--gn) 1%, transparent) 100%); padding: 16px; border-bottom: 0.5px solid color-mix(in srgb, var(--ink) 6%, transparent);">
      <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 12px;">
        <div style="width: 36px; height: 36px; border-radius: 50%; background: color-mix(in srgb, var(--gn) 10%, transparent); display: flex; align-items: center; justify-content: center;">
          <i class="ti ti-check" style="font-size: 18px; color: var(--gn);"></i>
        </div>
        <div style="flex: 1; min-width: 0;">
          <div style="font-size: 13px;">
            <button data-action="open-user" data-user-id="${u.telegram_id || ''}" style="font-weight: 500; background: none; border: none; padding: 0; cursor: pointer; color: var(--ink);">${esc(u.name || 'Оқырман')}</button>
            <span style="opacity: 0.7;"> кітап аяқтады</span>
          </div>
          <div style="font-size: 10.5px; opacity: 0.55;">${esc(post.time_ago || '')}</div>
        </div>
      </div>
      <button data-action="open-book" ${b.slug ? `data-slug="${esc(b.slug)}"` : ''} style="display: flex; gap: 12px; padding: 14px; background: var(--card); border: 0.5px solid color-mix(in srgb, var(--ink) 6%, transparent); border-radius: 12px; cursor: pointer; width: 100%; text-align: left;">
        <div style="width: 60px; height: 88px; background: ${esc(b.cover_gradient || 'linear-gradient(135deg, #1A0A2E 0%, #4A1E7C 100%)')}; border-radius: 4px; flex-shrink: 0; position: relative; overflow: hidden;">
          <div style="position: absolute; inset: 4px; border: 0.5px solid rgba(255,255,255,0.15);"></div>
          <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-family: Georgia, serif; font-size: 9px; font-style: italic; color: var(--bg); text-align: center; padding: 0 3px;">${esc(b.cover_text || b.title || '')}</div>
        </div>
        <div style="flex: 1; min-width: 0;">
          <div style="font-family: Georgia, serif; font-size: 15px; font-weight: 500; line-height: 1.2; margin-bottom: 4px;">${esc(b.title || '')}</div>
          <div style="font-size: 11px; opacity: 0.6; margin-bottom: 8px;">${esc(b.author || '')}${b.year ? ' · ' + b.year : ''}</div>
          <div style="display: flex; align-items: center; gap: 5px; font-size: 10.5px;">
            <span style="padding: 2px 8px; background: color-mix(in srgb, var(--gn) 10%, transparent); color: var(--gn); border-radius: 999px; font-weight: 500;">✓ Аяқтады</span>
            ${pagesStr ? `<span style="opacity: 0.55;">${pagesStr}</span>` : ''}
          </div>
        </div>
      </button>
      ${renderReactionsRow(post)}
    </div>`;
}

function renderPost(post) {
  if (post.post_type === 'quote') return renderQuotePost(post);
  if (post.post_type === 'book-finished') return renderBookFinishedPost(post);
  return renderReviewPost(post);
}

function renderPosts(posts, append) {
  const list = $('[data-field="posts-list"]', root());
  if (!list) return;
  const loadingBox = $('[data-field="feed-initial-loading"]', root());
  if (loadingBox) loadingBox.remove();
  if (!append) list.innerHTML = '';
  const html = posts.map(renderPost).join('');
  if (append) {
    list.insertAdjacentHTML('beforeend', html);
  } else {
    const emptyMsg = state.tab === 'friends'
      ? '<div style="padding: 40px 24px; text-align: center;"><div style="font-size:30px;margin-bottom:10px;">👥</div><div style="font-size: 13px; line-height:1.55; opacity: 0.72;">Әзірге ешкімді қадағаламайсың.<br>Оқырман профиліне кіріп <b style="opacity:1;color:var(--ac);">«Қадағалау»</b> бас — достарыңның рецензиялары осында шығады.</div></div>'
      : '<div style="padding: 40px 20px; text-align: center; font-size: 13px; opacity: 0.55; font-family: Georgia, serif; font-style: italic;">Әзірге пост жоқ.</div>';
    list.innerHTML = html || emptyMsg;
  }
}

// ── Modal lifecycle (.modal-ov reuse) ───────────────────────
export function openPostModal(post) {
  const modal = document.getElementById('feed-post-modal');
  if (!modal) return;
  fillPostModal(post);
  modal.classList.add('open');
  modal.dataset.modalOpen = 'true';
  document.body.style.overflow = 'hidden';
}

export function closePostModal() {
  const modal = document.getElementById('feed-post-modal');
  if (!modal) return;
  modal.classList.remove('open');
  modal.dataset.modalOpen = 'false';
  document.body.style.overflow = '';
}

function fillPostModal(post) {
  const u = post.user || {};
  const b = post.book || {};
  const c = post.content || {};
  const s = post.stats || {};

  // User
  const av = $('[data-field="modal-user-avatar"]');
  if (av) {
    av.textContent = u.initials || '?';
    av.style.background = u.color_gradient || 'linear-gradient(135deg, #C9A96E, #8B5E34)';
  }
  const nameEl = $('[data-field="modal-user-name"]');
  if (nameEl) nameEl.textContent = u.name || 'Оқырман';
  const timeEl = $('[data-field="modal-user-time"]');
  if (timeEl) timeEl.textContent = `${u.username ? '@' + u.username + ' · ' : ''}${post.time_ago || ''}`;
  const jandosEl = $('[data-field="modal-user-jandos"]');
  if (jandosEl) jandosEl.style.display = u.is_jandos ? '' : 'none';

  // Book
  const bookBtn = $('[data-field="modal-book-btn"]');
  if (bookBtn) bookBtn.dataset.slug = b.slug || '';
  const coverEl = $('[data-field="modal-book-cover"]');
  if (coverEl) coverEl.style.background = b.cover_gradient || 'linear-gradient(135deg, #8B5E34 0%, #6B4423 100%)';
  const coverTextEl = $('[data-field="modal-book-cover-text"]');
  if (coverTextEl) coverTextEl.textContent = b.cover_text || b.title || '';
  const titleEl = $('[data-field="modal-book-title"]');
  if (titleEl) titleEl.textContent = b.title || '';
  const authorEl = $('[data-field="modal-book-author"]');
  if (authorEl) authorEl.textContent = `${b.author || ''}${b.year ? ' · ' + b.year : ''}`;

  // Rating
  const ratingEl = $('[data-field="modal-rating"]');
  if (ratingEl) {
    if (c.rating) {
      const r = Math.max(0, Math.min(5, c.rating));
      const starsEl = $('[data-field="modal-rating-stars"]');
      if (starsEl) {
        starsEl.innerHTML = '<i class="ti ti-star-filled"></i>'.repeat(r) +
                             '<i class="ti ti-star" style="opacity:0.25"></i>'.repeat(5 - r);
      }
      const lblEl = $('[data-field="modal-rating-label"]');
      if (lblEl) lblEl.textContent = `${r}/5`;
      ratingEl.style.display = 'flex';
    } else {
      ratingEl.style.display = 'none';
    }
  }

  // Quote vs Text
  const quoteBlock = $('[data-field="modal-quote-block"]');
  const textEl = $('[data-field="modal-text"]');
  if (post.post_type === 'quote') {
    if (quoteBlock) {
      const qText = $('[data-field="modal-quote-text"]');
      if (qText) qText.textContent = `«${c.quote_text || c.text || ''}»`;
      const qMeta = $('[data-field="modal-quote-meta"]');
      if (qMeta) qMeta.textContent = `${b.author || ''} · «${b.title || ''}»${c.page_num ? ' · ' + c.page_num + '-бет' : ''}`;
      quoteBlock.style.display = '';
    }
    if (textEl) textEl.style.display = 'none';
  } else {
    if (quoteBlock) quoteBlock.style.display = 'none';
    if (textEl) {
      textEl.textContent = c.text || '';
      textEl.style.display = '';
    }
  }

  // Stats
  const likesEl = $('[data-field="modal-likes-count"]');
  if (likesEl) likesEl.textContent = fmtCount(s.likes || 0);
  const commentsEl = $('[data-field="modal-comments-count"]');
  if (commentsEl) commentsEl.textContent = fmtCount(s.comments || 0);

  // Like state
  const likeBtn = $('[data-field="modal-like-btn"]');
  if (likeBtn) {
    likeBtn.dataset.postType = post.post_type;
    likeBtn.dataset.postId = post.post_id;
    const likeIcon = $('[data-field="modal-like-icon"]');
    if (likeIcon) {
      likeIcon.className = `ti ${s.is_liked_by_me ? 'ti-heart-filled' : 'ti-heart'}`;
      likeIcon.style.color = s.is_liked_by_me ? '#f43f5e' : '';
      likeIcon.style.opacity = s.is_liked_by_me ? '1' : '0.7';
    }
    if (likesEl) {
      likesEl.style.color = s.is_liked_by_me ? '#f43f5e' : '';
      likesEl.style.fontWeight = s.is_liked_by_me ? '500' : '';
    }
  }

  // Save state
  const saveBtn = $('[data-field="modal-save-btn"]');
  if (saveBtn) {
    saveBtn.dataset.postType = post.post_type;
    saveBtn.dataset.postId = post.post_id;
    const saveIcon = $('[data-field="modal-save-icon"]');
    if (saveIcon) {
      saveIcon.className = `ti ${s.is_saved_by_me ? 'ti-bookmark-filled' : 'ti-bookmark'}`;
      saveIcon.style.color = s.is_saved_by_me ? '#8B5E34' : '';
      saveIcon.style.opacity = s.is_saved_by_me ? '1' : '0.7';
    }
  }
}

export async function expandPost(postType, postId) {
  if (!postType || !postId) return;
  try {
    const data = await api(`/api/feed/post/${encodeURIComponent(postType)}/${encodeURIComponent(postId)}`);
    openPostModal(data);
  } catch (e) {
    console.error('expandPost failed', e);
    toast('Пост табылмады');
  }
}

// ── Like / Save toggle ─────────────────────────────────────
function findPostLikeBtn(postType, postId) {
  return root().querySelector(`[data-action="post-like"][data-post-type="${postType}"][data-post-id="${postId}"]`);
}
function updateLikeUI(btn, isLiked, newCount) {
  if (!btn) return;
  const icon = btn.querySelector('i');
  const span = btn.querySelector('[data-field="post-likes-count"]');
  if (icon) {
    icon.className = `ti ${isLiked ? 'ti-heart-filled' : 'ti-heart'}`;
    icon.style.opacity = isLiked ? '1' : '0.7';
    icon.style.color = isLiked ? '#f43f5e' : '';
  }
  if (span) {
    span.textContent = String(newCount);
    span.style.color = isLiked ? '#f43f5e' : '';
    span.style.fontWeight = isLiked ? '500' : '';
  }
}

export async function postLike(postType, postId) {
  if (!postType || !postId) return;
  if (postType === 'book-finished') return;  // like жоқ
  // Optimistic UI
  const btn = findPostLikeBtn(postType, postId);
  const isLikedNow = btn?.querySelector('i.ti-heart-filled') != null;
  const span = btn?.querySelector('[data-field="post-likes-count"]');
  const currentCount = parseInt(span?.textContent || '0', 10);
  updateLikeUI(btn, !isLikedNow, currentCount + (isLikedNow ? -1 : 1));

  try {
    const res = await api(`/api/feed/post/${encodeURIComponent(postType)}/${encodeURIComponent(postId)}/like`, {
      method: 'POST',
    });
    if (res?.ok) {
      // Sync with server
      updateLikeUI(btn, res.action === 'liked', res.likes_count);
      // Modal sync (бұл пост ашық болса)
      const modal = document.getElementById('feed-post-modal');
      if (modal?.dataset.modalOpen === 'true') {
        const modalLike = $('[data-field="modal-like-btn"]');
        if (modalLike?.dataset.postId === String(postId) && modalLike?.dataset.postType === postType) {
          const icon = $('[data-field="modal-like-icon"]');
          if (icon) {
            icon.className = `ti ${res.action === 'liked' ? 'ti-heart-filled' : 'ti-heart'}`;
            icon.style.color = res.action === 'liked' ? '#f43f5e' : '';
          }
          const cnt = $('[data-field="modal-likes-count"]');
          if (cnt) cnt.textContent = res.likes_count;
        }
      }
    } else {
      // Revert
      updateLikeUI(btn, isLikedNow, currentCount);
    }
  } catch (e) {
    console.warn('postLike failed', e);
    updateLikeUI(btn, isLikedNow, currentCount);
    toast('Like жасалмады');
  }
}

function applySaveUI(icon, isSaved) {
  if (!icon) return;
  icon.className = `ti ${isSaved ? 'ti-bookmark-filled' : 'ti-bookmark'}`;
  icon.style.opacity = isSaved ? '1' : '0.7';
  icon.style.color = isSaved ? '#8B5E34' : '';
}

export async function postSave(postType, postId) {
  if (!postType || !postId) return;
  // Find both feed list button (if rendered) AND modal icon (if open)
  const sel = `[data-action="post-save"][data-post-type="${postType}"][data-post-id="${postId}"]`;
  const listBtn = root()?.querySelector(sel);
  const listIcon = listBtn?.querySelector('i');
  // Modal icon — data-field="modal-save-icon", parent btn has matching postType/postId
  const modalBtn = document.querySelector('[data-field="modal-save-btn"]');
  const modalIcon = (modalBtn?.dataset.postType === postType && String(modalBtn?.dataset.postId) === String(postId))
    ? document.querySelector('[data-field="modal-save-icon"]')
    : null;
  const refIcon = listIcon || modalIcon;
  const wasSaved = refIcon?.classList.contains('ti-bookmark-filled') || false;

  // Optimistic UI — both
  applySaveUI(listIcon, !wasSaved);
  applySaveUI(modalIcon, !wasSaved);

  try {
    const res = await api(`/api/feed/post/${encodeURIComponent(postType)}/${encodeURIComponent(postId)}/save`, {
      method: 'POST',
    });
    if (res?.ok) {
      const nowSaved = res.action === 'saved';
      applySaveUI(listIcon, nowSaved);
      applySaveUI(modalIcon, nowSaved);
    } else {
      applySaveUI(listIcon, wasSaved);
      applySaveUI(modalIcon, wasSaved);
    }
  } catch (e) {
    console.warn('postSave failed', e);
    applySaveUI(listIcon, wasSaved);
    applySaveUI(modalIcon, wasSaved);
    toast('Сақтау мүмкін болмады');
  }
}

// ── Spoiler reveal ──────────────────────────────────────────
export function revealSpoiler(el) {
  if (!el) return;
  el.dataset.spoilerHidden = 'false';
  el.style.background = '';
  el.style.border = '';
  el.style.padding = '0';
  el.innerHTML = '<div style="padding: 12px; font-size: 13px; opacity: 0.7; font-style: italic;">Спойлер ашылды. Толық мәтінді көру үшін постты ашыңыз.</div>';
}

// ── Tab switch ──────────────────────────────────────────────
export function feedTab(tab) {
  if (!tab || tab === state.tab) return;
  state.tab = tab;
  state.offset = 0;
  state.hasMore = true;
  renderTabs(tab);
  // Optimistic skeleton
  const list = $('[data-field="posts-list"]', root());
  if (list) {
    list.innerHTML = `<div style="padding: 40px 20px; text-align: center;"><i class="ti ti-loader-2" style="font-size: 28px; opacity: 0.4; animation: feed-spin 1s linear infinite;"></i></div>`;
  }
  loadPosts(tab, 0, false);
}

// ── Infinite scroll ─────────────────────────────────────────
function setupInfiniteScroll() {
  if (state.observer) state.observer.disconnect();
  const sentinel = $('[data-field="feed-sentinel"]', root());
  if (!sentinel) return;
  state.observer = new IntersectionObserver((entries) => {
    if (entries.some(e => e.isIntersecting) && state.hasMore && !state.loading) {
      loadPosts(state.tab, state.offset, true);
    }
  }, { rootMargin: '200px 0px 0px 0px' });
  state.observer.observe(sentinel);
}

// ── Core load ──────────────────────────────────────────────
async function loadPosts(tab, offset, append) {
  if (state.loading) return;
  state.loading = true;
  const myReqId = ++state.reqId;
  const loadingEl = $('[data-field="feed-loading"]', root());
  if (append && loadingEl) loadingEl.style.display = '';
  try {
    const data = await api(`/api/feed/v2?tab=${encodeURIComponent(tab)}&offset=${offset}&limit=20`);
    if (myReqId !== state.reqId) return;  // stale
    state.posts = append ? [...state.posts, ...(data.posts || [])] : (data.posts || []);
    state.offset = data.next_offset || (offset + (data.posts || []).length);
    state.hasMore = !!data.has_more;
    renderPosts(data.posts, append);
    const endEl = $('[data-field="feed-end"]', root());
    if (endEl) endEl.style.display = state.hasMore ? 'none' : '';
  } catch (e) {
    console.error('loadFeedV2 failed', e);
    if (!append) {
      const list = $('[data-field="posts-list"]', root());
      if (list) list.innerHTML = '<div style="padding: 40px 20px; text-align: center; font-size: 13px; opacity: 0.55;">Феед жүктелмеді.</div>';
    }
  } finally {
    if (myReqId === state.reqId) state.loading = false;
    if (loadingEl) loadingEl.style.display = 'none';
  }
}

// ── Action stubs ───────────────────────────────────────────
export function feedFilter() { toast('🎛 Сүзгі — жақын арада'); }
export function feedNewPost() { toast('✍ Жаңа пост — жақын арада'); }
export function postMenu() { toast('⋯ Пост менюі — жақын арада'); }
export function postShare() { toast('🔗 Бөлісу — жақын арада'); }
export function postComments() { toast('💬 Пікірлер — жақын арада'); }
export function openUser(id) {
  // Stage 7.7 fix: автордың PUBLIC профилін ашу (бұрын әрқашан өз профиліне кететін).
  if (!id) { show('profile'); return; }
  window._pendingPublicProfile = String(id);
  show('public-profile');
}

// ── ESC key — close modal (global, idempotent) ────────────
if (typeof window !== 'undefined' && !window._feedEscBound) {
  window._feedEscBound = true;
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const m = document.getElementById('feed-post-modal');
      if (m?.dataset.modalOpen === 'true') closePostModal();
    }
  });
}

// ── Main entry ──────────────────────────────────────────────
export async function loadFeedV2() {
  initOnboardingBanner();
  state.tab = 'all';
  state.offset = 0;
  state.hasMore = true;
  state.reqId = 0;
  await loadPosts('all', 0, false);
  setupInfiniteScroll();
}

// Legacy export — router сонда шақырады
export async function loadFeed() {
  return loadFeedV2();
}
